import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { accountCreationSlice } from "../Pages/AccountCreation/Slices/accountCreationSlice";
import { applicationReviewSlice } from "../Pages/Applications/Slices/ApplicationReviewSlice";
import { clientProfileCreationSlice } from "../Pages/Dashboard/Components/Slices/ClientProfileCreationSlice";
import { talentProfileCreationSlice } from "../Pages/Dashboard/Components/Slices/TalentProfileCreationSlice";
import { marketplaceSearchSlice } from "../Pages/Marketplace/Slices/marketplaceSearchSlice";
import { adminApi } from "./api/adminApi";
import { applicationApi } from "./api/applicationApi";
import { chatApi } from "./api/chatApi";
import { clientApi } from "./api/clientApi";
import { jobApi } from "./api/jobApi";
import { kaziAiApi } from "./api/kaziAiApi";
import { messageApi } from "./api/messageApi";
import { notificationApi } from "./api/notificationApi";
import { offerApi } from "./api/offerApi";
import { searchApi } from "./api/searchApi";
import { statisticsApi } from "./api/statisticsApi";
import { stripeApi } from "./api/stripeApi";
import { subscriptionApi } from "./api/subscriptionApi";
import { talentApi } from "./api/talentApi";
import { verificationApi } from "./api/verificationApi";
import { globalModalSlice } from "./slices/GlobalModalSlice";
import { globalSlice } from "./slices/GlobalSlice";
import { jobPostCreationSlice } from "./slices/JobPostCreationSlice";
import { jobPostingCreationSlice } from "./slices/JobPostingCreationSlice";
import { messagesSlice } from "./slices/MessagesSlice";
import { notificationSlice } from "./slices/NotificationSlice";
import { talentMarketplaceFiltersSlice } from "./slices/TalentMarketplaceFiltersSlice";
import { talentSearchSlice } from "./slices/TalentSearchSlice";
import { websocketSlice } from "./slices/WebsocketSlice";

const persistConfig = {
    key: 'root',
    storage,
    whitelist: [
        globalSlice.name,
        clientProfileCreationSlice.name
    ]
};

const rootReducer = combineReducers({
    // Slices
    [accountCreationSlice.name]: accountCreationSlice.reducer,
    [marketplaceSearchSlice.name]: marketplaceSearchSlice.reducer,
    [globalSlice.name] : globalSlice.reducer,
    [talentProfileCreationSlice.name] : talentProfileCreationSlice.reducer,
    [clientProfileCreationSlice.name] : clientProfileCreationSlice.reducer,
    [jobPostCreationSlice.name]: jobPostCreationSlice.reducer,
    [jobPostingCreationSlice.name]: jobPostingCreationSlice.reducer,
    [notificationSlice.name]: notificationSlice.reducer,
    [applicationReviewSlice.name]: applicationReviewSlice.reducer,
    [messagesSlice.name]: messagesSlice.reducer,
    [talentMarketplaceFiltersSlice.reducerPath]: talentMarketplaceFiltersSlice.reducer,
    [talentSearchSlice.reducerPath]: talentSearchSlice.reducer,
    [globalModalSlice.reducerPath]: globalModalSlice.reducer,
    [websocketSlice.reducerPath]: websocketSlice.reducer,
    // APIs
    [talentApi.reducerPath]: talentApi.reducer,
    [jobApi.reducerPath]: jobApi.reducer,
    [clientApi.reducerPath]: clientApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
    [applicationApi.reducerPath]: applicationApi.reducer,
    [adminApi.reducerPath]: adminApi.reducer,
    [offerApi.reducerPath]: offerApi.reducer,
    [messageApi.reducerPath]: messageApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [subscriptionApi.reducerPath]: subscriptionApi.reducer,
    [stripeApi.reducerPath]: stripeApi.reducer,
    [verificationApi.reducerPath]: verificationApi.reducer,
    [chatApi.reducerPath]: chatApi.reducer,
    [statisticsApi.reducerPath]: statisticsApi.reducer,
    [kaziAiApi.reducerPath]: kaziAiApi.reducer,
})

const persistedReducer = persistReducer(persistConfig, rootReducer)

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) => 
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            }
        })
        .concat([
            talentApi.middleware,
            clientApi.middleware,
            jobApi.middleware,
            searchApi.middleware,
            applicationApi.middleware,
            adminApi.middleware,
            offerApi.middleware,
            messageApi.middleware,
            notificationApi.middleware,
            subscriptionApi.middleware,
            stripeApi.middleware,
            verificationApi.middleware,
            chatApi.middleware,
            statisticsApi.middleware,
            kaziAiApi.middleware,
        ]),
})
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export default store
